import { StrictMode, useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import "../styles.css";
import {
  FindAccountScreen,
  LoginScreen,
  ResetPasswordScreen,
  SignupDoneScreen,
  SignupEmailScreen,
  SignupProfileScreen,
  SignupTermsScreen,
} from "./auth";
import {
  HomeScreen,
  NoticeDetailScreen,
  NoticesScreen,
  PackagingFormScreen,
  PackagingScreen,
  PaymentDetailScreen,
  PaymentHistoryScreen,
  PaymentScreen,
  PaymentSuccessScreen,
  PaymentFailScreen,
  ProductDetailScreen,
  ProductHomeScreen,
  ProductSearchScreen,
  QuoteDetailScreen,
  QuoteOfferDetailScreen,
  QuoteRequestScreen,
  QuotesScreen,
  RecommendationScreen,
  ShopScreen,
  UploadScreen,
  UploadLoadingScreen,
} from "./commerce";
import {
  InquiriesScreen,
  InquiryDetailScreen,
  InquiryWriteScreen,
  MyScreen,
  ProfileScreen,
} from "./my";
import { currentView, ViewName } from "./router";
import { getApiToken } from "../shared/api";

const AUTHENTICATED_VIEWS = new Set<ViewName>([
  "packagingForm",
  "upload",
  "uploadLoading",
  "recommendation",
  "quoteRequest",
  "quotes",
  "quoteDetail",
  "quoteOfferDetail",
  "payment",
  "paymentSuccess",
  "paymentFail",
  "paymentHistory",
  "paymentDetail",
  "my",
  "profile",
  "inquiries",
  "inquiryWrite",
  "inquiryDetail",
]);

function App() {
  const [view, setView] = useState<ViewName>(currentView());
  useEffect(() => {
    if (!window.history.state?.view)
      window.history.replaceState(
        { view: currentView() },
        "",
        window.location.href,
      );
    const sync = () => setView(currentView());
    window.addEventListener("popstate", sync);
    return () => window.removeEventListener("popstate", sync);
  }, []);

  if (AUTHENTICATED_VIEWS.has(view) && !getApiToken()) {
    return <LoginScreen />;
  }

  switch (view) {
    case "signupTerms":
      return <SignupTermsScreen />;
    case "signupProfile":
      return <SignupProfileScreen />;
    case "signupEmail":
      return <SignupEmailScreen />;
    case "signupDone":
      return <SignupDoneScreen />;
    case "findId":
      return <FindAccountScreen type="id" />;
    case "findPassword":
      return <FindAccountScreen type="password" />;
    case "resetPassword":
      return <ResetPasswordScreen />;
    case "home":
      return <HomeScreen />;
    case "productHome":
      return <ProductHomeScreen />;
    case "productSearch":
      return <ProductSearchScreen />;
    case "shop":
      return <ShopScreen />;
    case "productDetail":
      return <ProductDetailScreen />;
    case "notices":
      return <NoticesScreen />;
    case "noticeDetail":
      return <NoticeDetailScreen />;
    case "packaging":
      return <PackagingScreen />;
    case "packagingForm":
      return <PackagingFormScreen />;
    case "upload":
      return <UploadScreen />;
    case "uploadLoading":
      return <UploadLoadingScreen />;
    case "recommendation":
      return <RecommendationScreen />;
    case "quoteRequest":
      return <QuoteRequestScreen />;
    case "quotes":
      return <QuotesScreen />;
    case "quoteDetail":
      return <QuoteDetailScreen />;
    case "quoteOfferDetail":
      return <QuoteOfferDetailScreen />;
    case "payment":
      return <PaymentScreen />;
    case "paymentSuccess":
      return <PaymentSuccessScreen />;
    case "paymentFail":
      return <PaymentFailScreen />;
    case "paymentHistory":
      return <PaymentHistoryScreen />;
    case "paymentDetail":
      return <PaymentDetailScreen />;
    case "my":
      return <MyScreen />;
    case "profile":
      return <ProfileScreen />;
    case "inquiries":
      return <InquiriesScreen />;
    case "inquiryWrite":
      return <InquiryWriteScreen />;
    case "inquiryDetail":
      return <InquiryDetailScreen />;
    default:
      return <LoginScreen />;
  }
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
